import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import styled, { css } from 'styled-components'
import {
  Button,
  Modal,
  Input,
  Select,
  TimePicker,
  Switch,
  App,
  Spin,
  Tooltip,
  Empty,
  Popconfirm
} from 'antd'
import {
  LeftOutlined,
  RightOutlined,
  SettingOutlined,
  CopyOutlined,
  SaveOutlined,
  PlusOutlined,
  DeleteOutlined,
  CalendarOutlined,
  TableOutlined,
  ClearOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { useAuth } from '../hooks/AuthContext'
import { usePermission } from '../hooks/usePermission'
import api from '../utils/api'
import { now as tzNow } from '../utils/timezone'

/* ════════════════════════════════════════════════════════
   班表
   ────────────────────────────────────────────────────────
   設計取向：讓店長能像填紙本班表一樣快。
   1. 上方選一個班別當「畫筆」，接著在格子上點或拖曳就整排填好，
      不用一格一格開下拉選單。
   2. 每個班別有自己的顏色，一眼看得出誰早班誰晚班。
   3. 未排班的格子留白，且不計入上班天數 —— 舊系統把空白也算上班，
      會讓「這個月排了幾天」看起來是對的，其實只是還沒排。
   4. 最下面一列是每日上班人數，0 人的那天會標紅，避免整天沒人顧店。
   ════════════════════════════════════════════════════════ */

const BRAND = '#d5728a'

const Page = styled.div`
  padding: 20px 24px 40px;
  background: #fafafa;
  min-height: calc(100vh - 120px);

  @media (max-width: 768px) {
    padding: 12px;
  }

  /* 手機版儲存列固定在底部，內容要多留一段才不會被蓋住 */
  @media (max-width: 640px) {
    padding-bottom: calc(76px + env(safe-area-inset-bottom, 0px));
  }
`

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;

  /* 手機上按鈕會擠成好幾排，縮小間距並讓切換列自己佔一行 */
  @media (max-width: 640px) {
    gap: 8px;

    > button,
    .ant-btn {
      flex: 1 1 auto;
      min-width: 96px;
    }
  }
`

const Title = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  color: #222;
  margin-right: 8px;

  @media (max-width: 640px) {
    font-size: 19px;
    margin-right: 0;
  }
`

const MonthNav = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 999px;
  padding: 2px;

  .label {
    min-width: 110px;
    text-align: center;
    font-weight: 600;
    font-size: 15px;
    color: #333;
  }
`

/**
 * 儲存鈕在手機上的位置。
 *
 * 班表在手機上是往下捲很長的一張表，儲存鈕跟著標題列捲到畫面外之後，
 * 排完一整排要往回捲才找得到，很容易忘記存。所以手機版把它從標題列拿掉，
 * 改釘在畫面底部；桌機維持原本在標題列右側。
 */
const HeaderSave = styled.div`
  display: contents;

  @media (max-width: 640px) {
    display: none;
  }
`

const SaveBar = styled.div`
  display: none;

  @media (max-width: 640px) {
    display: flex;
    align-items: center;
    gap: 10px;
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 900;
    background: #fff;
    border-top: 1px solid #ececec;
    box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.06);
    padding: 10px 12px calc(10px + env(safe-area-inset-bottom, 0px));

    .count {
      flex: 1;
      min-width: 0;
      font-size: 13px;
      line-height: 1.4;
      color: #888;
    }

    .count strong {
      color: ${BRAND};
      font-size: 15px;
    }

    .ant-btn {
      flex: none;
      min-width: 128px;
      height: 40px;
    }
  }
`

const Spacer = styled.div`
  flex: 1;

  /* 手機不需要把右邊撐開，反而會讓按鈕被擠到下一行只剩半截 */
  @media (max-width: 640px) {
    flex-basis: 100%;
    height: 0;
  }
`

const Card = styled.div`
  background: #fff;
  border: 1px solid #eceff3;
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 14px;
`

const PaletteRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const PaletteHint = styled.div`
  font-size: 13px;
  color: #888;
  margin-top: 8px;
  line-height: 1.6;
`

/** 班別「畫筆」按鈕 */
const Chip = styled.button`
  border: 2px solid transparent;
  border-radius: 8px;
  padding: 7px 14px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  color: #fff;
  background: ${(p) => p.$color};
  opacity: ${(p) => (p.$active ? 1 : 0.45)};
  transition: all 0.15s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  line-height: 1.4;

  &:hover {
    opacity: 1;
  }

  ${(p) =>
    p.$active &&
    css`
      border-color: #222;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
    `}

  small {
    font-weight: 400;
    opacity: 0.9;
    font-size: 12px;
  }
`

const EraserChip = styled(Chip)`
  background: #fff;
  color: #666;
  border: 2px dashed ${(p) => (p.$active ? '#222' : '#d0d0d0')};
`

const TableWrap = styled.div`
  background: #fff;
  border: 1px solid #eceff3;
  border-radius: 12px;
  overflow: auto;
  /* 拖曳填入時避免選到文字 */
  user-select: none;
`

const Grid = styled.table`
  border-collapse: separate;
  border-spacing: 0;
  width: max-content;
  min-width: 100%;
  font-size: 13px;

  th,
  td {
    border-right: 1px solid #f0f2f5;
    border-bottom: 1px solid #f0f2f5;
    padding: 0;
    text-align: center;
  }

  /* 員工欄固定在左側，橫向捲動時才知道現在看的是誰 */
  .sticky-left {
    position: sticky;
    left: 0;
    z-index: 3;
    background: #fff;
    border-right: 2px solid #e8ecf1;
    text-align: left;
    padding: 8px 12px;
    min-width: 116px;
  }

  thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #fbfcfd;
    font-weight: 600;
    color: #555;
    padding: 6px 0;
    min-width: 44px;
  }

  @media (max-width: 640px) {
    font-size: 12px;

    thead th {
      min-width: 38px;
    }

    .sticky-left {
      min-width: 84px;
      padding: 6px 8px;
    }
  }

  thead th.sticky-left {
    z-index: 4;
  }
`

const DayHead = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.3;

  .d {
    font-size: 14px;
    color: ${(p) => (p.$weekend ? '#c96 ' : '#333')};
  }
  .w {
    font-size: 11px;
    color: #999;
  }
`

const HeadCell = styled.th`
  background: ${(p) =>
    p.$today ? '#fff1f4' : p.$weekend ? '#fcfaf7' : '#fbfcfd'} !important;
  box-shadow: ${(p) => (p.$today ? 'inset 0 -3px 0 ' + BRAND : 'none')};
`

const Cell = styled.td`
  cursor: ${(p) => (p.$editable ? 'pointer' : 'default')};
  background: ${(p) => (p.$weekend ? '#fcfaf7' : '#fff')};
  height: 40px;
`

const CellInner = styled.div`
  height: 38px;
  margin: 1px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  color: ${(p) => (p.$filled ? '#fff' : '#c8ccd2')};
  background: ${(p) => (p.$filled ? p.$color : 'transparent')};
  border: ${(p) => (p.$filled ? 'none' : '1px dashed #e6e9ee')};
  transition: transform 0.08s ease;

  ${(p) =>
    p.$off &&
    css`
      /* 休假用「淡底 + 同色邊框與文字」，跟上班的實心色塊一眼分得開，
         而且格子裡的字仍然讀得清楚（斜線底紋會把字吃掉） */
      background: ${p.$color}1f;
      border: 1.5px solid ${p.$color};
      color: ${p.$color};
    `}

  ${(p) =>
    p.$partial &&
    css`
      /* 這天另有「部分時段休假」（只存在員工休假，班表格子表達不了）。
         線上預約會被那段擋掉，所以格子上一定要看得出來 ——
         否則就是「班排了、服務也綁了，前台卻開不出時段」而查不到原因。
         用左下角的紅點，跟右上角的 $stale 橘點分開，兩個同時出現也不會打架。 */
      position: relative;

      &::before {
        content: '';
        position: absolute;
        bottom: 2px;
        left: 2px;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #e07a7a;
        box-shadow: 0 0 0 1.5px #fff;
      }
    `}

  ${(p) =>
    p.$stale &&
    css`
      /* 這格存下來的時間，跟現在的班別設定不一樣。
         前台線上預約讀的是這格的快照，不是班別設定，所以一定要看得出來，
         否則店家會以為改完班別設定，已排的班也跟著更新了。 */
      position: relative;

      &::after {
        content: '';
        position: absolute;
        top: 2px;
        right: 2px;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #f5a524;
        box-shadow: 0 0 0 1.5px #fff;
      }
    `}

  &:hover {
    transform: ${(p) => (p.$editable ? 'scale(1.06)' : 'none')};
  }
`

const NameCell = styled.div`
  .n {
    font-weight: 600;
    color: #222;
    font-size: 14px;
  }
  .r {
    font-size: 11px;
    color: #999;
  }
`

const StatCell = styled.td`
  padding: 4px 10px !important;
  background: #fbfcfd;
  white-space: nowrap;
  font-size: 12px;

  .w {
    color: #2f7d5d;
    font-weight: 600;
  }
  .o {
    color: #c25b5b;
    font-weight: 600;
  }
  .u {
    color: #b0b4ba;
  }
`

const FooterCell = styled.td`
  background: #fbfcfd;
  font-size: 12px;
  font-weight: 600;
  color: ${(p) => (p.$zero ? '#d4380d' : '#5a6270')};
  padding: 6px 0 !important;
`

const ViewSwitch = styled.div`
  display: inline-flex;
  background: #f0f2f5;
  border-radius: 8px;
  padding: 3px;

  @media (max-width: 640px) {
    width: 100%;

    button {
      flex: 1;
      justify-content: center;
    }
  }

  button {
    border: none;
    background: transparent;
    padding: 6px 14px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 13px;
    color: #666;
    display: inline-flex;
    align-items: center;
    gap: 6px;

    &.active {
      background: #fff;
      color: ${BRAND};
      font-weight: 600;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    }
  }
`

/* ───────── 休假月曆 ───────── */

const CalGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;

  @media (max-width: 768px) {
    gap: 4px;
  }
`

const CalHead = styled.div`
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: ${(p) => (p.$weekend ? '#c96' : '#666')};
  padding-bottom: 4px;
`

const CalDay = styled.div`
  min-height: 92px;
  border: 1px solid #eceff3;
  border-radius: 10px;
  padding: 6px 8px;
  background: ${(p) =>
    p.$outside ? '#fafbfc' : p.$today ? '#fff6f8' : '#fff'};
  opacity: ${(p) => (p.$outside ? 0.45 : 1)};

  .num {
    font-size: 13px;
    font-weight: 600;
    color: ${(p) => (p.$today ? BRAND : '#444')};
    margin-bottom: 4px;
  }

  /* 手機一欄只有 40 幾 px，放不下「姓名・假別」的標籤（會被切成一堆刪節號）。
     這裡改成只放日期與色點，誰休假改看下面的清單。 */
  @media (max-width: 640px) {
    min-height: 46px;
    border-radius: 8px;
    padding: 4px 2px;
    display: flex;
    flex-direction: column;
    align-items: center;

    .num {
      font-size: 12px;
      margin-bottom: 2px;
    }
  }
`

const OffTag = styled.div`
  font-size: 11px;
  border-radius: 4px;
  padding: 2px 6px;
  margin-bottom: 3px;
  color: #fff;
  background: ${(p) => p.$color};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 640px) {
    display: none;
  }
`

/** 手機版：一個人一顆點，看得出「這天有幾個人休」 */
const DotRow = styled.div`
  display: none;

  @media (max-width: 640px) {
    display: flex;
    gap: 3px;
    flex-wrap: wrap;
    justify-content: center;
  }
`

const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${(p) => p.$color};
`

/** 手機版：月曆下方列出實際休假的人，取代塞不下的標籤 */
const OffList = styled.div`
  display: none;

  @media (max-width: 640px) {
    display: block;
    margin-top: 16px;
    border-top: 1px solid #f0f2f5;
    padding-top: 12px;
  }
`

const OffListRow = styled.div`
  display: flex;
  gap: 10px;
  padding: 9px 2px;
  border-bottom: 1px solid #f5f6f8;

  &:last-child {
    border-bottom: none;
  }

  .date {
    flex: none;
    /* 「9/12（六）」要放得下不換行，62px 會斷成兩行 */
    width: 78px;
    white-space: nowrap;
    font-size: 13px;
    font-weight: 600;
    color: ${(p) => (p.$today ? BRAND : '#444')};
  }

  .people {
    flex: 1;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
`

const PersonTag = styled.span`
  font-size: 12px;
  border-radius: 5px;
  padding: 3px 8px;
  background: ${(p) => p.$color}1f;
  border: 1px solid ${(p) => p.$color};
  color: ${(p) => p.$color};
  white-space: nowrap;
`

const OffListEmpty = styled.div`
  font-size: 13px;
  color: #aaa;
  padding: 10px 2px;
`

const AllWorkingHint = styled.div`
  font-size: 11px;
  color: #b7bcc3;
`

/* ════════════════════════════════════════════════════════ */

const WEEK_LABELS = ['日', '一', '二', '三', '四', '五', '六']

const SchedulePage = () => {
  const { businessId, user } = useAuth()
  const { can } = usePermission()
  const { message } = App.useApp()

  // 「今天」以店家時區判斷，否則深夜會高亮到錯誤的日期欄
  const today = tzNow().format('YYYY-MM-DD')
  /** 全店排班（店主／店長／櫃檯／設計師） */
  const canManageAll = can('schedule:manage')
  /** 只能排自己（一般員工）：只能動自己那一列、而且只能動今天以後 */
  const canManageOwn = can('schedule:manage_own')
  /** 畫面上是否有任何一格可以動 —— 決定要不要顯示畫筆與儲存鈕 */
  const editable = canManageAll || canManageOwn
  const myId = String(user?.userId || user?._id || '')

  /**
   * 這一格能不能塗。只有 schedule:manage_own 的人，
   * 後端也會擋一次（403），這裡先擋是為了不要讓人白畫一輪才被退回。
   */
  const canPaint = useCallback(
    (empId, date) => {
      if (canManageAll) return true
      if (!canManageOwn) return false
      return String(empId) === myId && date >= today
    },
    [canManageAll, canManageOwn, myId, today]
  )

  const [month, setMonth] = useState(tzNow().format('YYYY-MM'))
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [view, setView] = useState('grid') // grid | calendar

  const [days, setDays] = useState([])
  const [employees, setEmployees] = useState([])
  const [shiftTypes, setShiftTypes] = useState([])
  const [suggested, setSuggested] = useState([])
  const [cells, setCells] = useState({}) // cells[empId][date] = {code, kind, ...}
  /**
   * 部分時段休假：partialOffs[empId][date] = [{ start, end, name }]
   *
   * 班表一天一格，只表達得了整天上班／整天休假；「9/17 10:00–12:20」這種
   * 半天行程只存在員工休假裡，但線上預約是讀它的。不標出來的話，店家會遇到
   * 「班明明排了、服務也綁了，前台就是開不出時段」而且完全查不到原因。
   */
  const [partialOffs, setPartialOffs] = useState({})

  /** 目前選中的「畫筆」：班別 code、'' 代表橡皮擦、null 代表沒選 */
  const [brush, setBrush] = useState(null)
  /** 尚未存檔的異動 'empId|date' → code（'' = 清除） */
  const [dirty, setDirty] = useState({})
  const painting = useRef(false)

  const [typeModalOpen, setTypeModalOpen] = useState(false)
  const [draftTypes, setDraftTypes] = useState([])

  const typeByCode = useMemo(
    () => new Map(shiftTypes.map((t) => [t.code, t])),
    [shiftTypes]
  )

  const load = useCallback(
    async (m) => {
      if (!businessId) return
      try {
        setLoading(true)
        const res = await api.get(`/api/businesses/${businessId}/schedule`, {
          params: { month: m }
        })
        const d = res.data?.data
        if (!d) return
        setDays(d.days || [])
        setEmployees(d.employees || [])
        setShiftTypes(d.shiftTypes || [])
        setSuggested(d.suggestedShiftTypes || [])
        setCells(d.cells || {})
        setPartialOffs(d.partialDayOffs || {})
        setDirty({})
      } catch (err) {
        message.error(err.response?.data?.error || '載入班表失敗')
      } finally {
        setLoading(false)
      }
    },
    [businessId, message]
  )

  useEffect(() => {
    load(month)
  }, [load, month])

  // 放開滑鼠就結束拖曳，即使已經移出表格
  useEffect(() => {
    const stop = () => {
      painting.current = false
    }
    window.addEventListener('mouseup', stop)
    window.addEventListener('touchend', stop)
    return () => {
      window.removeEventListener('mouseup', stop)
      window.removeEventListener('touchend', stop)
    }
  }, [])

  /** 這格當天有沒有部分時段休假（線上預約會被這段擋掉） */
  const partialOffOf = useCallback(
    (empId, date) => partialOffs[empId]?.[date] || [],
    [partialOffs]
  )

  /** 目前這格顯示的內容（未存檔的異動優先） */
  const cellValue = useCallback(
    (empId, date) => {
      const key = `${empId}|${date}`
      if (key in dirty) {
        const code = dirty[key]
        return code ? typeByCode.get(code) || null : null
      }
      const c = cells[empId]?.[date]
      if (!c) return null
      const t = typeByCode.get(c.code)
      if (!t) return c

      /**
       * 顯示樣式（label、顏色）跟著現行班別設定走，改配色才會即時反映；
       * 但「時間」一律以這格排班當下存下來的快照為準。
       *
       * EmployeeShift.start/end 是刻意存快照的（見 model 註解），店家事後改
       * 班別設定時，已排好的班不會跟著變 —— 而前台線上預約讀的正是這份快照。
       * 這裡如果拿現行設定去蓋掉快照，班表就會顯示一個「前台根本不認」的時間，
       * 店家會完全看不出為什麼客人約不到。不一致的格子改用 stale 標出來。
       */
      const stale =
        t.kind === 'shift' &&
        ((c.start || '') !== (t.start || '') || (c.end || '') !== (t.end || ''))

      return {
        ...t,
        start: c.start || '',
        end: c.end || '',
        note: c.note,
        stale,
        typeStart: t.start || '',
        typeEnd: t.end || ''
      }
    },
    [cells, dirty, typeByCode]
  )

  const paint = useCallback(
    (empId, date) => {
      if (brush === null || !canPaint(empId, date)) return
      setDirty((prev) => ({ ...prev, [`${empId}|${date}`]: brush }))
    },
    [brush, canPaint]
  )

  /** 整列（某人整個月）／整欄（某天全部人）快速填 —— 一樣只填得到自己有權限的格子 */
  const paintRow = (empId) => {
    if (brush === null) return
    setDirty((prev) => {
      const next = { ...prev }
      days.forEach((d) => {
        if (canPaint(empId, d)) next[`${empId}|${d}`] = brush
      })
      return next
    })
  }
  const paintColumn = (date) => {
    if (brush === null) return
    setDirty((prev) => {
      const next = { ...prev }
      employees.forEach((e) => {
        if (canPaint(e.id, date)) next[`${e.id}|${date}`] = brush
      })
      return next
    })
  }

  const dirtyCount = Object.keys(dirty).length

  const save = async () => {
    if (!dirtyCount) return
    try {
      setSaving(true)
      const payload = Object.entries(dirty).map(([k, code]) => {
        const [employeeId, date] = k.split('|')
        return { employeeId, date, code: code || null }
      })
      await api.put(`/api/businesses/${businessId}/schedule`, {
        cells: payload
      })
      message.success(`已儲存 ${payload.length} 格`)
      await load(month)
    } catch (err) {
      message.error(err.response?.data?.error || '儲存失敗')
    } finally {
      setSaving(false)
    }
  }

  const copyLastMonth = async () => {
    try {
      setSaving(true)
      const from = dayjs(`${month}-01`).subtract(1, 'month').format('YYYY-MM')
      const res = await api.post(`/api/businesses/${businessId}/schedule/copy`, {
        fromMonth: from,
        toMonth: month,
        overwrite: false
      })
      const n = res.data?.data?.copied || 0
      message.success(
        n ? `已從 ${from} 複製 ${n} 格（只填空白的格子）` : `${from} 沒有可複製的排班`
      )
      await load(month)
    } catch (err) {
      message.error(err.response?.data?.error || '複製失敗')
    } finally {
      setSaving(false)
    }
  }

  /* ───── 班別設定 ───── */

  const openTypeModal = () => {
    setDraftTypes(
      (shiftTypes.length ? shiftTypes : suggested).map((t) => ({ ...t }))
    )
    setTypeModalOpen(true)
  }

  const saveTypes = async () => {
    try {
      const res = await api.put(
        `/api/businesses/${businessId}/schedule/shift-types`,
        { shiftTypes: draftTypes }
      )
      message.success('班別設定已儲存')
      setTypeModalOpen(false)
      await load(month)

      /**
       * 已排好的班存的是「排班當下的時間快照」，改班別設定不會回頭動它們。
       * 但店家的認知通常是「我把晚班改到 21:00，之後的班就是到 21:00」，
       * 而前台線上預約讀的正是快照 —— 不問一聲就會變成班表看起來排到 21:00、
       * 客人卻只約得到 18:00，而且畫面上完全看不出原因。
       */
      const affected = res.data?.data?.affected || []
      if (affected.length) askSyncFutureShifts(affected)
    } catch (err) {
      message.error(err.response?.data?.error || '儲存班別失敗')
    }
  }

  /** 問店家要不要把「今天以後」已排好的班一併改成新時間 */
  const askSyncFutureShifts = (affected) => {
    const total = affected.reduce((n, a) => n + a.count, 0)
    Modal.confirm({
      title: '已排好的班要一起更新嗎？',
      width: 480,
      okText: `是，更新這 ${total} 天`,
      cancelText: '不用，維持原本時間',
      content: (
        <div style={{ fontSize: 13, lineHeight: 1.7 }}>
          <div style={{ marginBottom: 10 }}>
            班別時間改了，但<b>今天以後</b>已經排好的班還是照當初排的時間，
            <b>線上預約也會照當初的時間開放</b>：
          </div>
          {affected.map((a) => (
            <div
              key={a.code}
              style={{
                background: '#faf7f8',
                borderRadius: 6,
                padding: '8px 10px',
                marginBottom: 6
              }}
            >
              <b>{a.code}</b>　{a.from} → {a.to}
              <div style={{ color: '#888', marginTop: 2 }}>
                共 {a.count} 天（{a.dates[0]} ~ {a.dates[a.dates.length - 1]}）
              </div>
            </div>
          ))}
          <div style={{ color: '#888', marginTop: 10 }}>
            過去的班表不會被更動，保留當時實際的上班時間。
          </div>
        </div>
      ),
      onOk: async () => {
        try {
          const r = await api.put(
            `/api/businesses/${businessId}/schedule/shift-types/sync-future`,
            { codes: affected.map((a) => a.code) }
          )
          message.success(`已更新 ${r.data?.data?.updated || 0} 天的排班`)
          await load(month)
        } catch (err) {
          message.error(err.response?.data?.error || '更新排班失敗')
        }
      }
    })
  }

  const updateDraft = (idx, patch) =>
    setDraftTypes((prev) =>
      prev.map((t, i) => (i === idx ? { ...t, ...patch } : t))
    )

  /* ───── 統計（含尚未存檔的異動）───── */

  const stats = useMemo(() => {
    const out = {}
    employees.forEach((e) => {
      let work = 0
      let off = 0
      days.forEach((d) => {
        const v = cellValue(e.id, d)
        if (!v) return
        if (v.kind === 'off') off += 1
        else work += 1
      })
      out[e.id] = { work, off, unset: days.length - work - off }
    })
    return out
  }, [employees, days, cellValue])

  const dailyWork = useMemo(() => {
    const out = {}
    days.forEach((d) => {
      out[d] = employees.filter((e) => {
        const v = cellValue(e.id, d)
        return v && v.kind !== 'off'
      }).length
    })
    return out
  }, [days, employees, cellValue])

  /* ───── 休假月曆 ───── */

  const calendarCells = useMemo(() => {
    if (!days.length) return []
    const first = dayjs(`${month}-01`)
    const startPad = first.day()
    const total = first.daysInMonth()
    const out = []
    for (let i = 0; i < startPad; i++) {
      out.push({ date: first.subtract(startPad - i, 'day').format('YYYY-MM-DD'), outside: true })
    }
    for (let i = 0; i < total; i++) {
      out.push({ date: first.add(i, 'day').format('YYYY-MM-DD'), outside: false })
    }
    while (out.length % 7 !== 0) {
      const last = dayjs(out[out.length - 1].date).add(1, 'day')
      out.push({ date: last.format('YYYY-MM-DD'), outside: true })
    }
    return out
  }, [days, month])

  const offByDate = useMemo(() => {
    const out = {}
    days.forEach((d) => {
      out[d] = employees
        .map((e) => ({ e, v: cellValue(e.id, d) }))
        .filter((x) => x.v && x.v.kind === 'off')
        .map((x) => ({
          name: x.e.name,
          label: x.v.label || x.v.code,
          color: x.v.color || '#e07a7a'
        }))
    })
    return out
  }, [days, employees, cellValue])

  /* ═══════════════ render ═══════════════ */

  if (loading) {
    return (
      <Page>
        <div style={{ textAlign: 'center', padding: 80 }}>
          <Spin size='large' />
        </div>
      </Page>
    )
  }

  return (
    <Page>
      <Header>
        <Title>班表</Title>
        <MonthNav>
          <Button
            type='text'
            icon={<LeftOutlined />}
            onClick={() =>
              setMonth(dayjs(`${month}-01`).subtract(1, 'month').format('YYYY-MM'))
            }
          />
          <span className='label'>
            {dayjs(`${month}-01`).format('YYYY 年 M 月')}
          </span>
          <Button
            type='text'
            icon={<RightOutlined />}
            onClick={() =>
              setMonth(dayjs(`${month}-01`).add(1, 'month').format('YYYY-MM'))
            }
          />
        </MonthNav>
        {month !== tzNow().format('YYYY-MM') && (
          <Button size='small' onClick={() => setMonth(tzNow().format('YYYY-MM'))}>
            回本月
          </Button>
        )}

        <Spacer />

        <ViewSwitch>
          <button
            className={view === 'grid' ? 'active' : ''}
            onClick={() => setView('grid')}
          >
            <TableOutlined /> 月班表
          </button>
          <button
            className={view === 'calendar' ? 'active' : ''}
            onClick={() => setView('calendar')}
          >
            <CalendarOutlined /> 休假月曆
          </button>
        </ViewSwitch>

        {/* 班別設定與複製上月是整店層級的動作，只有能排全店班的人可以做 */}
        {canManageAll && (
          <>
            <Button icon={<SettingOutlined />} onClick={openTypeModal}>
              班別設定
            </Button>
            <Popconfirm
              title='從上個月複製班表'
              description='會依「星期幾」對齊複製，只填目前空白的格子，不會蓋掉已排好的。'
              okText='複製'
              cancelText='取消'
              onConfirm={copyLastMonth}
            >
              <Button icon={<CopyOutlined />}>複製上月</Button>
            </Popconfirm>
          </>
        )}
        {editable && (
          <>
            <HeaderSave>
              <Button
                type='primary'
                icon={<SaveOutlined />}
                loading={saving}
                disabled={!dirtyCount}
                onClick={save}
                style={{ background: dirtyCount ? BRAND : undefined }}
              >
                {dirtyCount ? `儲存 ${dirtyCount} 項變更` : '已儲存'}
              </Button>
            </HeaderSave>
          </>
        )}
      </Header>

      {!shiftTypes.length && (
        <Card>
          <div style={{ marginBottom: 10, color: '#555' }}>
            還沒有設定班別。先建立你們店裡實際在用的班別（例如「早班 10:00–15:00」），
            之後排班就是點一下的事。
          </div>
          {canManageAll && (
            <Button type='primary' style={{ background: BRAND }} onClick={openTypeModal}>
              建立班別
            </Button>
          )}
        </Card>
      )}

      {view === 'grid' && shiftTypes.length > 0 && editable && (
        <Card>
          <PaletteRow>
            <span style={{ color: '#555', fontWeight: 600, marginRight: 4 }}>
              快速填入
            </span>
            {shiftTypes.map((t) => (
              <Chip
                key={t.code}
                $color={t.color || BRAND}
                $active={brush === t.code}
                onClick={() => setBrush(brush === t.code ? null : t.code)}
              >
                {t.code}
                {t.kind === 'shift' && t.start && (
                  <small>
                    {t.start}–{t.end}
                  </small>
                )}
              </Chip>
            ))}
            <EraserChip
              $color='#fff'
              $active={brush === ''}
              onClick={() => setBrush(brush === '' ? null : '')}
            >
              <ClearOutlined /> 清除
            </EraserChip>
          </PaletteRow>
          <PaletteHint>
            選好上面的班別後，在表格裡<b>點一下</b>就填入；<b>按著左右拖曳</b>可以連續填一整排。
            {canManageAll ? (
              <>
                點<b>員工名字</b>填滿他整個月，點<b>日期</b>填滿那天所有人。
              </>
            ) : (
              <>
                點<b>自己的名字</b>可以填滿自己接下來的班。你的權限只能排
                <b>自己今天以後</b>的班，別人的格子與已經過去的日期都會是唯讀的。
              </>
            )}
          </PaletteHint>
        </Card>
      )}

      {view === 'grid' &&
        (employees.length === 0 ? (
          <Card>
            <Empty description='這家店還沒有員工，先到「員工管理」新增' />
          </Card>
        ) : (
          <TableWrap>
            <Grid>
              <thead>
                <tr>
                  <th className='sticky-left'>員工</th>
                  {days.map((d) => {
                    const dd = dayjs(d)
                    const w = dd.day()
                    return (
                      <HeadCell
                        key={d}
                        $weekend={w === 0 || w === 6}
                        $today={d === today}
                        onClick={() => paintColumn(d)}
                        style={{
                          cursor: canManageAll && brush !== null ? 'pointer' : 'default'
                        }}
                      >
                        <DayHead $weekend={w === 0 || w === 6}>
                          <span className='d'>{dd.date()}</span>
                          <span className='w'>{WEEK_LABELS[w]}</span>
                        </DayHead>
                      </HeadCell>
                    )
                  })}
                  <th style={{ minWidth: 96 }}>上班／休假</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((e) => (
                  <tr key={e.id}>
                    <td
                      className='sticky-left'
                      onClick={() => paintRow(e.id)}
                      style={{
                        cursor:
                          brush !== null && canPaint(e.id, days[days.length - 1])
                            ? 'pointer'
                            : 'default'
                      }}
                    >
                      <NameCell>
                        <div className='n'>{e.name}</div>
                        <div className='r'>{roleLabel(e.role)}</div>
                      </NameCell>
                    </td>
                    {days.map((d) => {
                      const v = cellValue(e.id, d)
                      const w = dayjs(d).day()
                      const partial = partialOffOf(e.id, d)
                      const partialText = partial.length
                        ? `另有部分時段休假：${partial
                            .map((o) => `${o.start}–${o.end}`)
                            .join('、')}（線上預約在這段不會開放）`
                        : ''
                      return (
                        <Cell
                          key={d}
                          $weekend={w === 0 || w === 6}
                          $editable={canPaint(e.id, d) && brush !== null}
                          onMouseDown={() => {
                            painting.current = true
                            paint(e.id, d)
                          }}
                          onMouseEnter={() => {
                            if (painting.current) paint(e.id, d)
                          }}
                        >
                          <Tooltip
                            title={
                              [
                                v
                                  ? v.stale
                                    ? `${v.label || v.code} ${v.start}–${v.end}｜` +
                                      `這天排班時的時間是 ${v.start}–${v.end}，` +
                                      `目前「${v.code}」的班別設定是 ${v.typeStart}–${v.typeEnd}。` +
                                      '線上預約以這天的時間為準；要改成新的請重新填入這一格。'
                                    : `${v.label || v.code}${
                                        v.start ? ` ${v.start}–${v.end}` : ''
                                      }`
                                  : '未排班',
                                partialText
                              ]
                                .filter(Boolean)
                                .join('｜')
                            }
                          >
                            <CellInner
                              $filled={!!v}
                              $off={v?.kind === 'off'}
                              $color={v?.color || BRAND}
                              $stale={!!v?.stale}
                              $partial={partial.length > 0}
                              $editable={canPaint(e.id, d) && brush !== null}
                            >
                              {v ? v.code : '·'}
                            </CellInner>
                          </Tooltip>
                        </Cell>
                      )
                    })}
                    <StatCell>
                      <span className='w'>{stats[e.id]?.work ?? 0}</span>
                      <span style={{ color: '#ccc' }}> / </span>
                      <span className='o'>{stats[e.id]?.off ?? 0}</span>
                      {stats[e.id]?.unset > 0 && (
                        <span className='u'> （未排 {stats[e.id].unset}）</span>
                      )}
                    </StatCell>
                  </tr>
                ))}
                <tr>
                  <td className='sticky-left' style={{ fontWeight: 600, color: '#5a6270' }}>
                    當日上班人數
                  </td>
                  {days.map((d) => (
                    <FooterCell key={d} $zero={dailyWork[d] === 0}>
                      {dailyWork[d]}
                    </FooterCell>
                  ))}
                  <FooterCell />
                </tr>
              </tbody>
            </Grid>
          </TableWrap>
        ))}

      {view === 'calendar' && (
        <Card>
          <CalGrid style={{ marginBottom: 8 }}>
            {WEEK_LABELS.map((w, i) => (
              <CalHead key={w} $weekend={i === 0 || i === 6}>
                {w}
              </CalHead>
            ))}
          </CalGrid>
          <CalGrid>
            {calendarCells.map((c) => {
              const list = offByDate[c.date] || []
              return (
                <CalDay
                  key={c.date}
                  $outside={c.outside}
                  $today={c.date === today}
                >
                  <div className='num'>{dayjs(c.date).date()}</div>
                  {!c.outside && (
                    <>
                      {list.length ? (
                        list.map((o, i) => (
                          <OffTag key={i} $color={o.color}>
                            {o.name}・{o.label}
                          </OffTag>
                        ))
                      ) : (
                        <AllWorkingHint>全員上班</AllWorkingHint>
                      )}
                      {/* 手機版只顯示色點，名字改列在月曆下方 */}
                      <DotRow>
                        {list.map((o, i) => (
                          <Dot key={i} $color={o.color} />
                        ))}
                      </DotRow>
                    </>
                  )}
                </CalDay>
              )
            })}
          </CalGrid>

          <OffList>
            {days.filter((d) => (offByDate[d] || []).length).length === 0 ? (
              <OffListEmpty>這個月還沒有排任何休假</OffListEmpty>
            ) : (
              days
                .filter((d) => (offByDate[d] || []).length)
                .map((d) => (
                  <OffListRow key={d} $today={d === today}>
                    <span className='date'>
                      {dayjs(d).format('M/D')}（{WEEK_LABELS[dayjs(d).day()]}）
                    </span>
                    <span className='people'>
                      {offByDate[d].map((o, i) => (
                        <PersonTag key={i} $color={o.color}>
                          {o.name}・{o.label}
                        </PersonTag>
                      ))}
                    </span>
                  </OffListRow>
                ))
            )}
          </OffList>
        </Card>
      )}

      {/* ───────── 班別設定 ───────── */}
      <Modal
        title='班別設定'
        open={typeModalOpen}
        onCancel={() => setTypeModalOpen(false)}
        onOk={saveTypes}
        okText='儲存'
        cancelText='取消'
        width={720}
        okButtonProps={{ style: { background: BRAND } }}
      >
        <div style={{ color: '#888', fontSize: 13, marginBottom: 12 }}>
          代號會直接顯示在班表格子裡，建議 2–3 個字。
          上班的班別要填時間，線上預約才知道那天可以約到幾點；休假類型不用填時間。
        </div>
        {draftTypes.map((t, idx) => (
          <div
            key={idx}
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              marginBottom: 8,
              flexWrap: 'wrap'
            }}
          >
            <Input
              style={{ width: 90 }}
              placeholder='代號'
              value={t.code}
              maxLength={6}
              onChange={(e) => updateDraft(idx, { code: e.target.value })}
            />
            <Input
              style={{ width: 160 }}
              placeholder='完整名稱'
              value={t.label}
              onChange={(e) => updateDraft(idx, { label: e.target.value })}
            />
            <Select
              style={{ width: 100 }}
              value={t.kind}
              onChange={(v) => updateDraft(idx, { kind: v })}
              options={[
                { value: 'shift', label: '上班' },
                { value: 'off', label: '休假' }
              ]}
            />
            {t.kind === 'shift' ? (
              <TimePicker.RangePicker
                format='HH:mm'
                minuteStep={15}
                allowEmpty={[true, true]}
                value={[
                  t.start ? dayjs(t.start, 'HH:mm') : null,
                  t.end ? dayjs(t.end, 'HH:mm') : null
                ]}
                onChange={(v) =>
                  updateDraft(idx, {
                    start: v?.[0] ? v[0].format('HH:mm') : '',
                    end: v?.[1] ? v[1].format('HH:mm') : ''
                  })
                }
              />
            ) : (
              <span style={{ width: 210, color: '#bbb', fontSize: 12 }}>
                休假為整天，不需填時間
              </span>
            )}
            <input
              type='color'
              value={t.color || BRAND}
              onChange={(e) => updateDraft(idx, { color: e.target.value })}
              style={{
                width: 34,
                height: 30,
                border: '1px solid #e0e0e0',
                borderRadius: 6,
                padding: 2,
                background: '#fff'
              }}
            />
            <Tooltip title={t.isActive === false ? '已停用' : '啟用中'}>
              <Switch
                size='small'
                checked={t.isActive !== false}
                onChange={(v) => updateDraft(idx, { isActive: v })}
              />
            </Tooltip>
            <Button
              type='text'
              danger
              icon={<DeleteOutlined />}
              onClick={() =>
                setDraftTypes((prev) => prev.filter((_, i) => i !== idx))
              }
            />
          </div>
        ))}
        <Button
          icon={<PlusOutlined />}
          onClick={() =>
            setDraftTypes((prev) => [
              ...prev,
              {
                code: '',
                label: '',
                kind: 'shift',
                start: '10:00',
                end: '18:00',
                color: BRAND,
                isActive: true,
                sortOrder: prev.length + 1
              }
            ])
          }
          style={{ marginTop: 4 }}
        >
          新增班別
        </Button>
      </Modal>

      {/* 手機版：儲存鈕釘在畫面底部，捲多遠都按得到 */}
      {editable && (
        <SaveBar>
          <div className='count'>
            {dirtyCount ? (
              <>
                有 <strong>{dirtyCount}</strong> 格尚未儲存
              </>
            ) : (
              '所有變更都已儲存'
            )}
          </div>
          <Button
            type='primary'
            icon={<SaveOutlined />}
            loading={saving}
            disabled={!dirtyCount}
            onClick={save}
            style={{ background: dirtyCount ? BRAND : undefined }}
          >
            {dirtyCount ? '儲存變更' : '已儲存'}
          </Button>
        </SaveBar>
      )}
    </Page>
  )
}

function roleLabel(role) {
  return (
    {
      owner: '店主',
      manager: '店長',
      designer: '設計師',
      receptionist: '櫃檯',
      accountant: '會計',
      employee: '員工'
    }[role] || '員工'
  )
}

export default SchedulePage
